import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import DemobilizationPopup from "../DemobilizationPopup/DemobilizationPopup";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  DEMOBILIZATION_COLUMNS,
  DEMOBILIZATION_CONFIG,
  DEMOBILIZATION_SEARCHABLE_FIELDS,
  ERE_STATUSES
} from './DemobilizationTabConstants';

const DemobilizationTab = ({ setTrigger, trigger }) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage (consider using context for better performance)
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Data tracking state for UI optimization
  const [dataState, setDataState] = useState(() => ({
    hasData: false,
    isLoading: true,
    totalRecords: 0
  }));

  // Optimized state management - simplified to match original logic
  const [checkedItems, setCheckedItems] = useState([]);

  const [popupState, setPopupState] = useState(() => ({
    demobilize: false,
  }));

  // Data load handler to track data availability
  const handleDataLoad = useCallback((data, pagination) => {
    const dataExists = data && data.length > 0;
    setDataState({
      hasData: dataExists,
      isLoading: pagination.isLoading,
      totalRecords: pagination.totalRecords || 0
    });
    
    // Clear selection if no data
    if (!dataExists) {
      setCheckedItems([]);
    }
  }, []);

  // Optimized checkbox handling - same logic as original, just optimized
  const handleCheckboxChange = useCallback((event, employee_id, item) => {
    const isChecked = event.target.checked;
    
    setCheckedItems((prevState) => {
      // Ensure prevState is always an array
      const currentState = prevState || [];
      
      if (isChecked) {
        // Add the item if it is checked and not already present
        if (!currentState.some((checkedItem) => checkedItem.employee_id === employee_id)) {
          return [...currentState, { employee_id, item }];
        } else {
          return currentState;
        }
      } else {
        // Remove the item if it is unchecked
        return currentState.filter((checkedItem) => checkedItem.employee_id !== employee_id);
      }
    });
  }, []);

  // Optimized custom cell renderer with memoization
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return item.visa_provider || item.Overseas_visa_provider || "-";
      
      case 'select': {
        // Check if employee should be disabled for selection (optimized check)
        const isDisabled = ERE_STATUSES.includes(item.emergency_leave_status) ||
          ERE_STATUSES.includes(item.annual_vacation_status);

        if (isDisabled) return null;

        return (
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={checkedItems.some(checkedItem => checkedItem.employee_id === item.candidate_id)}
            onChange={(event) => handleCheckboxChange(event, item.candidate_id, item)}
          />
        );
      }
      
      default:
        return null;
    }
  }, [checkedItems, handleCheckboxChange]);

  // Optimized demobilize handler
  const handleDemobilizeClick = useCallback(() => {
    if (checkedItems.length === 0) {
      dispatch(setAppError({
        msg: "Please select atleast one employee to demobilize",
      }));
      return;
    }
    setPopupState(prev => ({ ...prev, demobilize: true }));
  }, [checkedItems.length, dispatch]);

  // Optimized API callback - now uses trigger pattern
  const apiCall = useCallback(() => {
    setCheckedItems([]);
    // Trigger refresh using the trigger pattern
    if (setTrigger) {
      setTrigger(prev => !prev);
    }
  }, [setTrigger]);

  // Memoized custom actions with data-based disabling
  const customActions = useMemo(() => {
    const actions = [];
    
    if (checkAccess("DEMOBILIZATION", "active-edit")) {
      actions.push({
        label: `Demobilize (${checkedItems.length})`,
        onClick: handleDemobilizeClick,
        disabled: !dataState.hasData || checkedItems.length === 0,
        className: 'btn btn-sm text-white border-none',
        style: {
          backgroundColor: (!dataState.hasData || checkedItems.length === 0) ? '#9ca3af' : '#2563eb',
          cursor: (!dataState.hasData || checkedItems.length === 0) ? 'not-allowed' : 'pointer'
        }
      });
    }
    
    return actions;
  }, [checkedItems.length, handleDemobilizeClick, dataState.hasData, dashboardData]);

  // Optimized download handlers
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: `Excel Downloaded (${result.recordCount} records)`,
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
      color: "error"
    }));
  }, [dispatch]);

  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={DEMOBILIZATION_CONFIG.API_ENDPOINT}
        downloadEndpoint={DEMOBILIZATION_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={DEMOBILIZATION_COLUMNS}
        searchableFields={DEMOBILIZATION_SEARCHABLE_FIELDS}
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={DEMOBILIZATION_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={DEMOBILIZATION_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={DEMOBILIZATION_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        enableSearch={true}
        searchPlaceholder={DEMOBILIZATION_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={DEMOBILIZATION_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Custom Actions
        customActions={customActions}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDataLoad={handleDataLoad}
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={DEMOBILIZATION_CONFIG.NO_DATA_MESSAGE}
        className="demobilization-table"
      />

      {/* Optimized popup rendering */}
      {popupState.demobilize && (
        <DemobilizationPopup
          PopUp={popupState.demobilize}
          setPopUp={(value) => setPopupState(prev => ({ ...prev, demobilize: value }))}
          title="Update Border Number"
          url="/employees/updateBorderNumbersFromExcel"
          callBack={apiCall}
          empid={checkedItems}
          editfunctionality={false}
          searchResponse={[]}
        />
      )}
    </div>
  );
};

// PropTypes for type safety
DemobilizationTab.propTypes = {
  setTrigger: PropTypes.func,
  trigger: PropTypes.bool
};

DemobilizationTab.displayName = 'DemobilizationTab';

export default DemobilizationTab;
